"use client";

import React, { Children, isValidElement } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import Image from "next/image";
import { IMPACT_SCORE_LABELS } from "@/app/types/book";

interface MarkdownProps {
  content: string;
  className?: string;
}

export function Markdown({ content, className = "" }: MarkdownProps) {
  return (
    <div
      className={`prose prose-sm max-w-none dark:prose-invert [&_pre]:bg-gray-100 [&_pre]:dark:bg-gray-800 [&_pre]:p-4 [&_pre]:rounded-lg ${className}`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight, rehypeRaw]}
        components={{
          h1: ({ ...props }) => (
            <h1 className="text-3xl  mb-4 font-light" {...props} />
          ),
          h2: ({ ...props }) => (
            <h2
              className="text-xl font-light mb-3 mt-8 tracking-wide uppercase"
              {...props}
            />
          ),
          h3: ({ ...props }) => (
            <h3
              className="text-lg mb-2 mt-8 font-light tracking-wider"
              {...props}
            />
          ),
          p: ({ ...props }) => <p className="mb-4" {...props} />,
          ul: ({ ...props }) => (
            <ul className="list-disc ml-6 mb-4" {...props} />
          ),
          ol: ({ ...props }) => (
            <ol className="list-decimal ml-6 mb-4" {...props} />
          ),
          li: ({ ...props }) => <li className="mb-1" {...props} />,
          blockquote: ({ ...props }) => (
            <blockquote
              className="border-l-4 border-gray-300 pl-4 italic my-4"
              {...props}
            />
          ),
          a: ({ ...props }) => (
            <a className="text-blue-500 hover:underline" {...props} />
          ),
          img: ({ src, alt = "" }) => (
            <Image
              src={src || ""}
              alt={alt}
              width={800}
              height={600}
              className="w-full max-w-2xl mx-auto"
            />
          ),
          code: (props: any) => {
            const isInline =
              !props.node?.position?.start?.column ||
              props.node?.position?.start?.line ===
                props.node?.position?.end?.line;

            if (isInline) {
              return (
                <code
                  className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm font-mono"
                  {...props}
                >
                  {props.children}
                </code>
              );
            }
            return <code {...props}>{props.children}</code>;
          },
          table: ({ ...props }) => (
            <table className="table-banded w-full text-left" {...props} />
          ),
          th: ({ ...props }) => <th className="text-left !text-left" style={{ textAlign: 'left' }} {...props} />,
          tr: ({ ...props }) => <tr {...props} />,
          td: ({ children, className = "", ...props }) => {
            let impactLevel: keyof typeof IMPACT_SCORE_LABELS | undefined;
            Children.forEach(children, (child) => {
              if (
                isValidElement(child) &&
                (child.props as any)["data-impact-level"]
              ) {
                impactLevel = (child.props as any)["data-impact-level"];
              }
            });

            let tdClass = `text-left !text-left ${className}`.trim();
            if (impactLevel && IMPACT_SCORE_LABELS[impactLevel]) {
              const label = IMPACT_SCORE_LABELS[impactLevel];
              tdClass += ` ${label.priorityColorBackground} ${label.priorityColorBorder} bg-opacity-50`;
            }

            return (
              <td className={tdClass} style={{ textAlign: "left" }} {...props}>
                {children}
              </td>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
